import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, Stack, Tab, Tabs, Typography } from '@mui/material';
import ListData from '../table-elements/list-data.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { LISTING_COLUMNS } from '../../config/module-configs/manage-videos.config';
import { getChannelAssignRulesetVideos } from '../../services/ruleset.service';
import { getListData } from '../../services/videos.service';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import { CONTENT_SCHEDULE_TYPE } from '../../config/const.config';
import { SCHEDULED_VIDEOS_LIST } from '../../config/module-configs/content-videos.config';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListExecutedRulesetsVideos = ({
  title,
  channelContentScheduleInfoId,
  channelId,
}) => {
  const { getSignal, isAbortError } = useAbortableRequest();
  const [activeTab, setActiveTab] = useState('testRun');
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [videosList, setVideosList] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    columns: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
  });

  const handlePageChange = (v) => {
    setVideosList({
      ...videosList,
      page: v,
      reloadCounter: videosList.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setVideosList({
      ...videosList,
      rowsPerPage: v,
      page: 0,
      reloadCounter: videosList.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setVideosList({
      ...videosList,
      sortBy,
      sortOrder,
      reloadCounter: videosList.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    const signal = getSignal();
    const params = [];
    params.push(`page=${videosList.page + 1}`);
    params.push(`perPage=${videosList.rowsPerPage}`);
    if (videosList.sortBy && videosList.sortOrder) {
      params.push(`sortBy=${videosList.sortBy}`);
      params.push(`sortOrder=${videosList.sortOrder}`);
    }
    params.push(`type=${CONTENT_SCHEDULE_TYPE.ASSIGNMENT}`);
    if (activeTab === 'productionAssignment') {
      const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
      getListData(
        paramsQuery,
        {
          id: channelId,
          videoIds: [],
          from: 'channelVideoAssignment',
          action: 'view',
          filter: [],
        },
        signal
      )
        .then((res) => {
          if (!signal.aborted) {
            setVideosList({
              ...videosList,
              loading: false,
              totalRows: res.data.totalRows || 0,
              columns: [
                ...LISTING_COLUMNS,
                {
                  id: 'series_name',
                  label: 'Series',
                  width: '25%',
                  align: 'center',
                  dataKey: 'series_name',
                  sortable: true,
                  type: CELL_DATA_TYPES.TEXT,
                },
                {
                  id: 'isManuallyAssigned',
                  label: 'Manually Assigned',
                  width: '25%',
                  align: 'center',
                  dataKey: 'isManuallyAssigned',
                  sortable: true,
                  type: CELL_DATA_TYPES.BOOL,
                },
              ],
              rows: res.data.rows || [],
              error: false,
            });
          }
        })
        .catch((err) => {
          if (isAbortError(err)) {
            return;
          }
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';

          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
        });
    } else {
      const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
      getChannelAssignRulesetVideos(
        channelContentScheduleInfoId,
        channelId,
        paramsQuery,
        signal
      )
        .then((res) => {
          if (!signal.aborted) {
            setVideosList({
              ...videosList,
              loading: false,
              totalRows: res.data.totalRows || 0,
              columns: SCHEDULED_VIDEOS_LIST.filter(
                (col) => col.dataKey !== 'scheduledDate'
              ),
              rows: res.data.rows || [],
              error: false,
            });
          }
        })
        .catch((err) => {
          if (isAbortError(err)) {
            return;
          }
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';

          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
        });
    }
  }, [videosList.reloadCounter]);

  return (
    <Box marginY={2} height="100%">
      <Typography variant="h6" display="flex" alignItems="center">
        {title}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        marginBottom={2}
      >
        <Box display="flex" alignItems="center">
          <Tabs
            value={activeTab}
            aria-label="tabs"
            indicatorColor="primary"
            onChange={(_, newValue) => {
              setVideosList({
                ...videosList,
                sortBy: '',
                sortOrder: '',
                page: 0,
                reloadCounter: videosList.reloadCounter + 1,
                loading: true,
              });
              setActiveTab(newValue);
            }}
          >
            <Tab value="productionAssignment" label="Production Assignment" />
            <Tab value="testRun" label="Dry Run Result" />
          </Tabs>
        </Box>
      </Stack>

      <Card
        sx={{
          maxHeight: '85%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <ListData
          columns={videosList.columns}
          rows={videosList.rows}
          page={videosList.page}
          rowsPerPage={videosList.rowsPerPage}
          totalRows={videosList.totalRows}
          loading={videosList.loading}
          actions={[]}
          error={videosList.error}
          sortBy={videosList.sortBy}
          sortOrder={videosList.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
        />
      </Card>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

ListExecutedRulesetsVideos.propTypes = {
  title: PropTypes.string.isRequired,
  channelContentScheduleInfoId: PropTypes.number.isRequired,
  channelId: PropTypes.number.isRequired,
};

export default ListExecutedRulesetsVideos;
